import React, { useState, useEffect } from 'react';
import api, { extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function LogViewer() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [logLevel, setLogLevel] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [systemDebugInfo, setSystemDebugInfo] = useState(null);

  useEffect(() => {
    // Set default fromDate to today at 00:00
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setFromDate(formattedDate);
  }, []);

  const fetchSystemDebugInfo = async () => {
    try {
      const response = await api.get('/admin/logs/debug');
      if (response.data.success) {
        setSystemDebugInfo(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching system debug info:', err);
      const errorDetails = extractErrorDetails(err, 'Error al obtener información de diagnóstico');
      setError(errorDetails.message);
      setErrorTrace(errorDetails.errorTrace || '');
    }
  };

  const handleGenerateTestLogs = async () => {
    try {
      const response = await api.post('/admin/logs/test');
      if (response.data.success) {
        alert('✓ Logs de prueba generados exitosamente\n\n' + response.data.data.join('\n'));
        // Refresh debug info after generating test logs
        fetchSystemDebugInfo();
      } else {
        setError(response.data.message || 'Error al generar logs de prueba');
      }
    } catch (err) {
      console.error('Error generating test logs:', err);
      const errorDetails = extractErrorDetails(err, 'Error al generar logs de prueba');
      setError(errorDetails.message);
      setErrorTrace(errorDetails.errorTrace || '');
    }
  };

  const handleFetchLogs = async () => {
    if (!fromDate) {
      setError('Fecha desde es requerida');
      setErrorTrace('');
      setDebugInfo(null);
      return;
    }

    setLoading(true);
    setError('');
    setErrorTrace('');
    setDebugInfo(null);
    
    try {
      const params = new URLSearchParams();
      params.append('fromDate', fromDate);
      if (toDate) {
        params.append('toDate', toDate);
      }
      if (logLevel) {
        params.append('level', logLevel);
      }
      if (debugMode) {
        params.append('debug', 'true');
      }

      const response = await api.get(`/admin/logs?${params.toString()}`);
      
      if (response.data.success) {
        setLogs(response.data.data || []);
      } else {
        setError(response.data.message || 'Error al cargar los logs');
        setErrorTrace(response.data.errorTrace || '');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      const errorDetails = extractErrorDetails(err, 'Error al cargar los logs. Verifique que la tabla log_entries exista en la base de datos.');
      setError(errorDetails.message);
      setErrorTrace(errorDetails.errorTrace || '');
      setDebugInfo(errorDetails.debugInfo);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLogs = async () => {
    if (!fromDate) {
      setError('Fecha desde es requerida');
      setErrorTrace('');
      setDebugInfo(null);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('fromDate', fromDate);
      if (toDate) {
        params.append('toDate', toDate);
      }
      if (logLevel) {
        params.append('level', logLevel);
      }

      const response = await api.get(`/admin/logs/download?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `logs_${fromDate}_to_${toDate || 'now'}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading logs:', err);
      const errorDetails = extractErrorDetails(err, 'Error al descargar los logs. Verifique que la tabla log_entries exista en la base de datos.');
      setError(errorDetails.message);
      setErrorTrace(errorDetails.errorTrace || '');
      setDebugInfo(errorDetails.debugInfo);
    }
  };

  return (
    <div className="log-viewer-container">
      <h2>Visor de Logs</h2>
      
      <div className="log-filters">
        <div className="input-group">
          <label htmlFor="fromDate">Fecha Desde*</label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            required
          />
        </div>
        
        <div className="input-group">
          <label htmlFor="toDate">Fecha Hasta (opcional)</label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        
        <div className="input-group">
          <label htmlFor="logLevel">Nivel de Log (opcional)</label>
          <select
            id="logLevel"
            value={logLevel}
            onChange={(e) => setLogLevel(e.target.value)}
          >
            <option value="">Todos los niveles</option>
            <option value="TRACE">TRACE</option>
            <option value="DEBUG">DEBUG</option>
            <option value="INFO">INFO</option>
            <option value="WARN">WARN</option>
            <option value="ERROR">ERROR</option>
          </select>
        </div>
        
        <div className="input-group">
          <label>
            <input
              type="checkbox"
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
            />
            {' '}Modo Debug
          </label>
        </div>
        
        <div className="button-group">
          <button 
            onClick={handleFetchLogs} 
            className="btn btn-primary"
            disabled={loading || !fromDate}
          >
            {loading ? 'Cargando...' : 'Buscar Logs'}
          </button>
          
          <button 
            onClick={handleDownloadLogs} 
            className="btn btn-secondary"
            disabled={!fromDate}
          >
            📥 Descargar TXT
          </button>
          
          <button 
            onClick={fetchSystemDebugInfo} 
            className="btn btn-info"
          >
            🔍 Ver Info Sistema
          </button>
          
          <button 
            onClick={handleGenerateTestLogs} 
            className="btn btn-warning"
          >
            🧪 Generar Logs de Prueba
          </button>
        </div>
      </div>

      {systemDebugInfo && systemDebugInfo.length > 0 && (
        <div className="system-debug-info">
          <h3>📊 Información del Sistema de Logs</h3>
          <pre className="debug-output">
            {systemDebugInfo.join('\n')}
          </pre>
        </div>
      )}

      {error && (
        <ErrorDisplay 
          error={error} 
          errorTrace={errorTrace}
          debugInfo={debugInfo}
          className="log-viewer-error-display"
        />
      )}

      <div className="log-content">
        {logs.length > 0 ? (
          <pre className="log-output">
            {logs.join('\n')}
          </pre>
        ) : (
          !loading && <p className="no-logs">No hay logs para mostrar. Haga clic en "Buscar Logs" para cargar.</p>
        )}
      </div>

      <style jsx>{`
        .log-viewer-container {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .log-filters {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: flex-end;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .input-group label {
          font-weight: 600;
          font-size: 14px;
        }

        .input-group input,
        .input-group select {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .input-group select {
          background-color: white;
          cursor: pointer;
        }

        .button-group {
          display: flex;
          gap: 10px;
        }

        .system-debug-info {
          margin-bottom: 20px;
          border: 2px solid #007bff;
          border-radius: 4px;
          background: #e7f3ff;
          padding: 15px;
        }

        .system-debug-info h3 {
          margin: 0 0 10px 0;
          color: #0056b3;
        }

        .debug-output {
          margin: 0;
          padding: 10px;
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
          max-height: 300px;
          overflow-y: auto;
        }

        .log-content {
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f8f9fa;
          min-height: 400px;
          max-height: 600px;
          overflow: auto;
        }

        .log-output {
          margin: 0;
          padding: 15px;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .no-logs {
          padding: 40px;
          text-align: center;
          color: #666;
        }

        .log-viewer-error-display {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 15px;
          border: 1px solid #f5c6cb;
        }
        
        .log-viewer-error-display .error-message {
          margin: 0;
          white-space: pre-wrap;
        }
      `}</style>
    </div>
  );
}

export default LogViewer;
